import React, {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EntityListSelectActions } from 'actions';
import NoData from 'components/SvgIcons/emptyScreen/NoData';
import NewEntityList from 'containers/components/NewEntityList';
import { getLiteral } from 'utils/getLiteral';

import LeadPreviewRow from './LeadPreviewRow';
import { LEADS_PREVIEW_CHECKBOX_COL_ID, LEADS_PREVIEW_ENTITY } from './constants';

const SCROLL_RESTORE_DELAYS_MS = [0, 16, 50, 120, 250];

const findScrollableParent = (element) => {
    let node = element?.parentElement;
    while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight
        ) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
};

const getGridViewport = (tableRef) => tableRef.current?.getApi?.()?.gridBodyCtrl?.eBodyViewport;

const LeadList = (
    { activeBucket, activeLeads, isDuplicatesEmpty, selectedIds, onSelectionChange },
    ref,
) => {
    const dispatch = useDispatch();
    const listRootRef = useRef(null);
    const tableRef = useRef(null);
    const activeLeadsRef = useRef(activeLeads);
    activeLeadsRef.current = activeLeads;
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;
    const previousLeadIdsRef = useRef('');
    const scrollRestoreRef = useRef(null);
    const scrollRestoreTimersRef = useRef([]);
    const shouldClearWeb5Checkboxes = useSelector(
        (state) => state.entityListSelect?.[LEADS_PREVIEW_ENTITY.entity]?.shouldClearWeb5Checkboxes,
    );

    const clearScheduledScrollRestore = useCallback(() => {
        scrollRestoreTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
        scrollRestoreTimersRef.current = [];
    }, []);

    const restoreScrollPosition = useCallback(() => {
        const pending = scrollRestoreRef.current;
        if (!pending) return;

        const api = tableRef.current?.getApi?.();
        if (pending.rowIndex != null && api?.ensureIndexVisible) {
            api.ensureIndexVisible(pending.rowIndex, 'top');
        }

        const viewport = getGridViewport(tableRef);
        if (viewport && pending.scrollTop > 0) {
            viewport.scrollTop = pending.scrollTop;
        }

        if (pending.fallbackScrollTop != null && listRootRef.current) {
            const scrollParent = findScrollableParent(listRootRef.current);
            if (scrollParent) {
                scrollParent.scrollTop = pending.fallbackScrollTop;
            }
        }
    }, []);

    const scheduleScrollRestore = useCallback(() => {
        if (!scrollRestoreRef.current) return;

        clearScheduledScrollRestore();
        restoreScrollPosition();
        requestAnimationFrame(restoreScrollPosition);
        requestAnimationFrame(() => requestAnimationFrame(restoreScrollPosition));

        scrollRestoreTimersRef.current = SCROLL_RESTORE_DELAYS_MS.map((delayMs) =>
            window.setTimeout(() => {
                if (!scrollRestoreRef.current) return;
                restoreScrollPosition();
                if (delayMs === SCROLL_RESTORE_DELAYS_MS[SCROLL_RESTORE_DELAYS_MS.length - 1]) {
                    scrollRestoreRef.current = null;
                }
            }, delayMs),
        );
    }, [clearScheduledScrollRestore, restoreScrollPosition]);

    const captureScrollPosition = useCallback(() => {
        const api = tableRef.current?.getApi?.();
        const firstDisplayedRow = api?.getFirstDisplayedRow?.();
        const viewport = getGridViewport(tableRef);
        const scrollParent = listRootRef.current ? findScrollableParent(listRootRef.current) : null;

        scrollRestoreRef.current = {
            scrollTop: viewport?.scrollTop ?? 0,
            rowIndex:
                Number.isFinite(firstDisplayedRow) && firstDisplayedRow >= 0
                    ? firstDisplayedRow
                    : null,
            fallbackScrollTop: scrollParent?.scrollTop ?? null,
        };
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            captureScrollPosition,
        }),
        [captureScrollPosition],
    );

    useEffect(() => () => clearScheduledScrollRestore(), [clearScheduledScrollRestore]);

    const resetLeadsPreviewSelection = useCallback(() => {
        dispatch(EntityListSelectActions.resetListSelect(LEADS_PREVIEW_ENTITY, true));
        dispatch(EntityListSelectActions.setShouldClearWeb5Checkboxes(LEADS_PREVIEW_ENTITY, true));
    }, [dispatch]);

    useEffect(() => {
        dispatch(
            EntityListSelectActions.init({
                entity: LEADS_PREVIEW_ENTITY,
                selectionFor: 'leadsProspecting',
                selectionEntity: 'leads',
                isLocal: true,
            }),
        );
        dispatch(EntityListSelectActions.setShouldClearWeb5Checkboxes(LEADS_PREVIEW_ENTITY, true));

        return () => {
            resetLeadsPreviewSelection();
        };
    }, [dispatch, resetLeadsPreviewSelection]);

    useEffect(() => {
        if (!shouldClearWeb5Checkboxes) return undefined;
        tableRef.current?.clearCheckboxes?.();
        dispatch(EntityListSelectActions.setShouldClearWeb5Checkboxes(LEADS_PREVIEW_ENTITY, false));
        return undefined;
    }, [dispatch, shouldClearWeb5Checkboxes]);

    const handleOnRef = useCallback((gridRef) => {
        tableRef.current = gridRef;
    }, []);

    useEffect(() => {
        const colId = LEADS_PREVIEW_CHECKBOX_COL_ID;
        const rows = activeLeads.reduce((acc, lead) => {
            acc[lead.id] = selectedIds.some((sid) => String(sid) === String(lead.id));
            return acc;
        }, {});
        const areAllChecked =
            activeLeads.length > 0 &&
            activeLeads.every((lead) => selectedIds.some((sid) => String(sid) === String(lead.id)));
        dispatch(
            EntityListSelectActions.setCheckedWeb5(LEADS_PREVIEW_ENTITY, {
                [colId]: {
                    rows,
                    areAllChecked,
                    lazyAreAllChecked: areAllChecked,
                },
            }),
        );
    }, [activeLeads, dispatch, selectedIds]);

    const components = useMemo(
        () => ({
            LeadPreviewRow,
        }),
        [],
    );

    const onClickCheckbox = useCallback(
        ({ id, value, checked, data, rowIndex }) => {
            const isChecked = typeof value === 'boolean' ? value : Boolean(checked);

            onSelectionChange((current) =>
                isChecked ? [...new Set([...current, id])] : current.filter((item) => item !== id),
            );
            dispatch(
                EntityListSelectActions.onEntitySelect(
                    LEADS_PREVIEW_ENTITY,
                    id,
                    isChecked,
                    data,
                    rowIndex,
                    checked,
                ),
            );
        },
        [dispatch, onSelectionChange],
    );

    const toggleRow = useCallback(
        (data, nextChecked) => {
            const colId = LEADS_PREVIEW_CHECKBOX_COL_ID;
            const rows = activeLeadsRef.current.reduce((acc, lead) => {
                const isThisRow = String(lead.id) === String(data.id);
                acc[lead.id] = isThisRow
                    ? nextChecked
                    : selectedIdsRef.current.some((sid) => String(sid) === String(lead.id));
                return acc;
            }, {});
            const areAllChecked =
                activeLeadsRef.current.length > 0 &&
                activeLeadsRef.current.every((lead) => rows[lead.id]);
            const checkedWeb5 = {
                [colId]: {
                    rows,
                    areAllChecked,
                    lazyAreAllChecked: areAllChecked,
                },
            };
            onClickCheckbox({
                id: data.id,
                isColumn: false,
                value: nextChecked,
                checked: checkedWeb5,
                data,
                rowIndex: data.rowIndex,
                total: activeLeadsRef.current.length,
            });
        },
        [onClickCheckbox],
    );

    const emptyViewProps = useMemo(() => {
        if (!isDuplicatesEmpty) return undefined;

        const title = getLiteral('label_no_leads_duplicated_title');
        const subtitle = getLiteral('label_no_leads_duplicated_desc');

        return {
            title,
            subtitle,
            titleFirstTime: title,
            subtitleFirstTime: subtitle,
            icon: <NoData />,
            iconFirstTime: <NoData />,
            canCreate: false,
            hideClearButton: true,
        };
    }, [isDuplicatesEmpty]);

    const listConfig = useMemo(
        () => ({
            entity: LEADS_PREVIEW_ENTITY,
            useFiltersDrawer: true,
            withEntitySelection: true,
            selectionFor: 'leadsProspecting',
            selectionEntity: 'leads',
            isLocalSelection: true,
            columnDefs: [
                {
                    colId: LEADS_PREVIEW_CHECKBOX_COL_ID,
                    headerName: '',
                    field: 'name',
                    headerComponent: 'headerTextCell',
                    headerComponentParams: {
                        hideTitle: true,
                    },
                    cellRenderer: 'LeadPreviewRow',
                    cellRendererParams: {
                        onToggleLead: toggleRow,
                    },
                    flex: 1,
                    minWidth: 280,
                    resizable: false,
                    sortable: false,
                    lockPosition: true,
                    suppressMovable: true,
                },
            ],
        }),
        [toggleRow],
    );

    const rows = useMemo(
        () =>
            activeLeads.map((lead, rowIndex) => ({
                ...lead,
                Id: lead.id,
                rowIndex,
            })),
        [activeLeads],
    );

    const leadIdsKey = useMemo(
        () => activeLeads.map((lead) => String(lead.id)).join('\u0001'),
        [activeLeads],
    );

    useLayoutEffect(() => {
        const previousLeadIds = previousLeadIdsRef.current;
        const isAppend =
            previousLeadIds.length > 0 && leadIdsKey.startsWith(`${previousLeadIds}\u0001`);

        previousLeadIdsRef.current = leadIdsKey;

        if (isAppend && scrollRestoreRef.current) {
            scheduleScrollRestore();
        }
    }, [leadIdsKey, scheduleScrollRestore]);

    const handleRowDataUpdated = useCallback(() => {
        tableRef.current?.getApi?.()?.refreshCells?.({ force: true });
        if (scrollRestoreRef.current) {
            restoreScrollPosition();
        }
    }, [restoreScrollPosition]);

    const innerData = useMemo(() => ({ data: rows, loading: false, error: false }), [rows]);

    const getCheckedRows = useCallback(
        (data) => ({
            [LEADS_PREVIEW_CHECKBOX_COL_ID]: {
                rows: data.reduce((acc, current) => {
                    acc[current.id] = selectedIds.some((sid) => String(sid) === String(current.id));
                    return acc;
                }, {}),
                areAllChecked:
                    !!data.length &&
                    data.every((lead) =>
                        selectedIds.some((sid) => String(sid) === String(lead.id)),
                    ),
                lazyAreAllChecked:
                    !!data.length &&
                    data.every((lead) =>
                        selectedIds.some((sid) => String(sid) === String(lead.id)),
                    ),
            },
        }),
        [selectedIds],
    );

    return (
        <div className="fm-leads-modal__list" ref={listRootRef}>
            <NewEntityList
                key={activeBucket}
                id="leads-preview-list"
                entity={LEADS_PREVIEW_ENTITY}
                handleOnRef={handleOnRef}
                config={listConfig}
                emptyViewProps={emptyViewProps}
                innerData={innerData}
                components={components}
                initOnReady={false}
                useLazyLoad={false}
                useCache={false}
                useSort={false}
                useSelectColumns={false}
                useFieldSelector={false}
                getCheckedRows={getCheckedRows}
                forceRowUpdating
                domLayout="normal"
                onRowDataUpdated={handleRowDataUpdated}
                headerHeight="0"
                rowHeight={72}
                loaderPlaceholderRowHeight={72}
                useFullWidthDivider
            />
        </div>
    );
};

export default memo(forwardRef(LeadList));
